import React, { useEffect, useRef, useState } from "react";
import '../../css/EmissionsReport.css';

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  ArcElement,
  DoughnutController,
  Title,
  Tooltip,
  Legend
} from "chart.js";

import { getCIIGradeColor } from '../../utils/ciiGradeMapper';

Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  ArcElement,
  DoughnutController,
  Title,
  Tooltip,
  Legend
);

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined || isNaN(num)) return '0.00';
  const numValue = Number(num);
  const fixed = numValue.toFixed(decimals);
  const parts = fixed.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

const generateReportId = (vesselName, timestamp) => {
  const cleanVesselName = (vesselName || 'VESSEL')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '_')
    .substring(0, 20);
  const date = new Date(timestamp);
  const dateStr = date.toISOString().split('T')[0].replace(/-/g, '');
  const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '');
  return `${cleanVesselName}_${dateStr}_${timeStr}`;
};

const fuelColors = {
  HFO: "#696c72",
  VLSFO: "#571900",
  ULSFO: "#eea473",
  LFO: "#723c1a",
  MDO: "#c9ac5d",
  "LPG-AVERAGE": "#f59e0b",
  "LPG-BUTANE": "#f97316",
  "LPG-PROPANE": "#b1794b",
  "LNG-BOILER": "#07c4c4",
  "LNG-OTHERS": "#9bcde2",
  "LNG-AE": "#a7c8ce",
  "LNG-ME": "#9ba7ad",
  METHANOL: "#22c55e",
  ETHANOL: "#84cc16",
  "BIO": "#10b981",
  "BIO-FUEL": "#10b981",
  B30: "#4ade80",
  BIOLNG: "#166534",
};

const greenShades = ["#166534","#15803d","#16a34a","#22c55e","#4ade80","#86efac"];

const getFuelColor = (fuel) => {
  if (!fuel) return "#64748b";
  const fuelUpper = fuel.toUpperCase();
  if (fuelColors[fuelUpper]) return fuelColors[fuelUpper];
  let hash = 0;
  for (let i = 0; i < fuelUpper.length; i++) {
    hash = fuelUpper.charCodeAt(i) + ((hash << 5) - hash);
  }
  return greenShades[Math.abs(hash) % greenShades.length];
};

const formatVesselType = (vesselType) => {
  if (!vesselType) return '';
  return vesselType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const EmissionsReport = ({ data }) => {
  const barRef = useRef(null);
  const pieRef = useRef(null);
  const barInstance = useRef(null);
  const pieInstance = useRef(null);
  const reportRef = useRef(null);
  const [reportId, setReportId] = useState(null);

  useEffect(() => {
    if (!data) return;
    const reportKey = `report_${data.imo_number}_${data.voyage_start_date}_${data.voyage_end_date}`;
    let storedTimestamp = sessionStorage.getItem(`${reportKey}_timestamp`);
    if (!storedTimestamp) {
      storedTimestamp = data.created_at || new Date().toISOString();
      sessionStorage.setItem(`${reportKey}_timestamp`, storedTimestamp);
    }
    setReportId(generateReportId(data.vessel_name, storedTimestamp));
  }, [data]);

  const downloadPDF = async () => {
    if (!reportRef.current || !reportId) return;
    const actionBar = reportRef.current.querySelector(".action-bar");
    if (actionBar) actionBar.style.display = "none";
    await new Promise((r) => setTimeout(r, 350));
    const fullCanvas = await html2canvas(reportRef.current, {
      scale: 6, useCORS: true, backgroundColor: "#f8fafc",
      logging: false, removeContainer: true, imageTimeout: 0, allowTaint: true,
    });
    if (actionBar) actionBar.style.display = "flex";
    const pdf      = new jsPDF({ orientation: "p", unit: "mm", format: "a4", compress: false });
    const pdfW     = pdf.internal.pageSize.getWidth();
    const pdfH     = pdf.internal.pageSize.getHeight();
    const margin   = 8;
    const contentW = pdfW - margin * 2;
    const pxPerMm    = fullCanvas.width / contentW;
    const pageSlotPx = (pdfH - margin * 2) * pxPerMm;
    const rawPages   = fullCanvas.height / pageSlotPx;
    const SINGLE_PAGE_THRESHOLD = 1.08;
    if (rawPages <= SINGLE_PAGE_THRESHOLD) {
      const naturalHmm = fullCanvas.height / pxPerMm;
      const maxH       = pdfH - margin * 2;
      const fitScale   = Math.min(1, maxH / naturalHmm);
      const drawH      = naturalHmm * fitScale;
      const drawW      = contentW   * fitScale;
      const offsetX    = margin + (contentW - drawW) / 2;
      pdf.addImage(fullCanvas.toDataURL("image/jpeg", 0.75), "JPEG", offsetX, margin, drawW, drawH);
    } else {
      const totalPages  = Math.ceil(rawPages);
      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = fullCanvas.width;
      for (let page = 0; page < totalPages; page++) {
        const srcY = Math.round(page * pageSlotPx);
        const srcH = Math.min(Math.round(pageSlotPx), fullCanvas.height - srcY);
        sliceCanvas.height = srcH;
        const ctx = sliceCanvas.getContext("2d");
        ctx.clearRect(0, 0, sliceCanvas.width, srcH);
        ctx.drawImage(fullCanvas, 0, srcY, fullCanvas.width, srcH, 0, 0, fullCanvas.width, srcH);
        const sliceHmm = srcH / pxPerMm;
        if (page > 0) pdf.addPage();
        pdf.addImage(sliceCanvas.toDataURL("image/jpeg", 0.75), "JPEG", margin, margin, contentW, sliceHmm);
      }
    }
    pdf.save(`${reportId}.pdf`);
  };

  // ── BAR CHART ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!data) return;

    const fuelEtsList = data.fuel_ets_list;

    if (barRef.current && fuelEtsList) {
      // All fuels from total_consumption keys
      const fuels = Object.keys(fuelEtsList.total_consumption || {});

console.log("FUEL ETS LIST:", fuelEtsList);      const datasets = fuels.map(fuel => ({
        label: fuel,
        data: [
          fuelEtsList.total_consumption?.[fuel] || 0,  
          fuelEtsList.eua_consumption?.[fuel]   || 0,  
          fuelEtsList.uka_consumption?.[fuel]   || 0,  
          fuelEtsList.feum_consumption?.[fuel]  || 0,  
        ],
        backgroundColor: getFuelColor(fuel),
      }));

      if (barInstance.current) barInstance.current.destroy();

      barInstance.current = new Chart(barRef.current, {
        type: 'bar',
        data: {
          labels: ['Total Consumption', 'EU ETS', 'UK ETS', 'FEUM'],
          datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: { boxWidth: 15, boxHeight: 15, padding: 10, font: { size: 12 } }
            },
            tooltip: { mode: 'index', intersect: false },
          },
          scales: {
            x: { stacked: true, grid: { display: false }, ticks: { color: '#4e4e4e' } },
            y: { stacked: true, grid: { color: '#e5e7eb' }, ticks: { color: '#4e4e4e' } },
          },
        },
      });
    }

    return () => { if (barInstance.current) barInstance.current.destroy(); };
  }, [data]);

  // ── PIE CHART — shows both consumption AND emission ─────────────────────────
  useEffect(() => {
    if (!data) return;

    const pieData = data.pie_chart_data;
    if (pieRef.current && pieData) {
      // Two datasets: consumption (inner ring) + emission (outer ring)
      const labels = pieData.map(f => f.fuel_name);
      const consumptionValues = pieData.map(f => f.consumption);
      const emissionValues    = pieData.map(f => f.total_emission);
      const colors            = labels.map(l => getFuelColor(l));

      if (pieInstance.current) pieInstance.current.destroy();

      pieInstance.current = new Chart(pieRef.current, {
        type: 'doughnut',
        data: {
          labels,
         datasets: [
  {
    label: 'Emission (MTCO₂)',
    data: emissionValues,
    backgroundColor: colors,
    borderWidth: 2,
    borderColor: '#ffffff',
  },
],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: { boxWidth: 15, boxHeight: 15, padding: 10, font: { size: 12 } }
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const datasetLabel = ctx.dataset.label || '';
                  return `${ctx.label} — ${datasetLabel}: ${formatNumber(ctx.parsed)}`;
                },
              },
            },
          },
        },
      });
    }

    return () => { if (pieInstance.current) pieInstance.current.destroy(); };
  }, [data]);

  if (!data) {
    return (
      <div className="report-container" style={{ padding: "40px", textAlign: "center" }}>
        <p>No report data available</p>
      </div>
    );
  }

  const ciiColor = getCIIGradeColor(data.grade);

  const hasIceClass = data.is_ice_class === true && (
    (data.ice_class_type &&
      data.ice_class_type !== '0' &&
      data.ice_class_type !== 0 &&
      data.ice_class_type !== '') ||
    (data.ice_class_distance &&
      data.ice_class_distance !== 0 &&
      data.ice_class_distance !== '0')
  );

  return (
    <div ref={reportRef} className="report-container">

      {/* ── HEADER ── */}
      <div className="report-header">
        <div className="header-content">
          <h1 className="report-title">Voyage Emissions &amp; Compliance Statement</h1>
          <div className="vessel-meta-line">
            <span className="vessel-name">{data.vessel_name?.toUpperCase() || ""}</span>
            <span className="separator-pipe">|</span>
            <span className="imo-number">IMO {data.imo_number || "1234567"}</span>
            <span className="separator-pipe">|</span>
            <span className="deadweight">
              {data.deadweight ? `${formatNumber(data.deadweight, 0)} DWT` : "75,000 DWT"}
            </span>
            {data.vessel_type && (
              <>
                <span className="separator-pipe">|</span>
                <span className="vessel-type">{formatVesselType(data.vessel_type)}</span>
              </>
            )}
            {hasIceClass && data.ice_class_type && (
              <>
                <span className="separator-pipe">|</span>
                <span className="ice-class-type">Ice Class: {data.ice_class_type}</span>
              </>
            )}
          </div>
          <div className="voyage-meta-line">
            <span className="route-info">
              {data.from_port || "Rotterdam"} → {data.to_port || "Aldgate"}
            </span>
            <span className="separator-pipe-light">|</span>
            <span className="spot-price-indicator">
              <span className="blinking-dot" />
              <span className="spot-text">Spot Price: €{data.spot_price || "0"}</span>
            </span>
          </div>
        </div>
        <div style={{ marginTop: "20px", position: "relative" }}>
          <img src="/logoi.png" className="report-img" style={{ width: "120px", height: "40px" }} />
        </div>
      </div>

      {/* ── KPI CARDS ── */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <label style={{ textTransform: "none" }}>GHG (gCO<sub>2</sub>eq/MJ)</label>
          <span className="kpi-value">{formatNumber(data.total_ghg)}</span>
        </div>
        <div className="kpi-card">
          <label style={{ textTransform: "none" }}>CB (MTCO<sub>2</sub>eq)</label>
          <span className="kpi-value">{formatNumber(data.cb / 1000000, 3)}</span>
        </div>
        <div className="kpi-card">
          <label>FEU Penalty (€)</label>
          <span className="kpi-value">{formatNumber(data.penalty || 0)}</span>
        </div>
        <div className="kpi-card">
          <label>Total EUA</label>
          <span className="kpi-value">{formatNumber(data.eua)}</span>
        </div>
        <div className="kpi-card">
          <label>Total UKA</label>
          <span className="kpi-value">{formatNumber(data.uka)}</span>
        </div>
        <div className="kpi-card">
          <label>Total Cost (€)</label>
          <span className="kpi-value">{formatNumber(data.total_cost)}</span>
        </div>
      </div>

      {/* ── MAIN GRID ── */}
      <div className="main-content-grid">
        <div className="left-column">
          <div className="chart-section chart-bar">
            <h3 className="section-title">Fuel Distribution (MT)</h3>
            <canvas ref={barRef} />
          </div>

          <div className="regulatory-section">
            <h3 className="section-title">Regulatory Performance</h3>
            <div className="regulatory-grid">
              <div className="regulatory-card">
                <div className="reg-value" style={{
                  color: getCIIGradeColor(data.grade).color,
                  backgroundColor: getCIIGradeColor(data.grade).bgColor,
                  borderRadius: '8px', display: 'inline-block'
                }}>
                  {data.grade || 'NA'}
                </div>
                <div className="reg-label">CII</div>
              </div>
              <div className="regulatory-card">
                <div className="reg-value" style={{
                  color: ciiColor.color, backgroundColor: ciiColor.bgColor,
                  borderRadius: '8px', display: 'inline-block',
                }}>
                  {formatNumber(data.cii)}
                </div>
                <div className="reg-label">AER</div>
              </div>
              <div className="regulatory-card">
                <div className="reg-value blue">{formatNumber(data.eeoi, 1)}</div>
                <div className="reg-label">EEOI INDEX</div>
              </div>
              <div className="regulatory-card">
                <div className="reg-value orange">{data.phase_in || '--'}%</div>
                <div className="reg-label">APPLICABLE COVERAGE</div>
              </div>
            </div>
            <div className="regulatory-grid" style={{ marginTop: '20px' }}>
              <div className="regulatory-card">
                <div className="reg-value blue">
                  {data.ghg_target ? formatNumber(data.ghg_target, 2) : 'NA'}
                </div>
                <div className="reg-label">GHG TARGET</div>
              </div>
              <div className="regulatory-card">
                <div className="reg-value orange">
                  {data.fwind !== undefined && data.fwind !== null ? formatNumber(data.fwind, 2) : 'NA'}
                </div>
                <div className="reg-label" style={{ textTransform: "none", fontStyle: "italic" }}>
                  f<sub>wind</sub>
                </div>
              </div>
              <div className="regulatory-card">
                <div className="reg-value" style={{
                  color: data.vessel_status === 'Deficit' ? '#dc2626' : '#16a34a',
                  borderRadius: '8px', display: 'inline-block',
                }}>
                  {data.vessel_status || 'NA'}
                </div>
                <div className="reg-label">FEU STATUS</div>
              </div>
            </div>
          </div>
        </div>

        <div className="right-column">
          <div className="chart-section chart-pie">
            <h3 className="section-title">Emission Distribution </h3>
           
            <canvas ref={pieRef} />
          </div>

          <div className="timeline-section">
            <h3 className="section-title">Voyage Operational Timeline</h3>
            <div className="timeline-content">
              <div className="timeline-row">
                <span className="timeline-label">Voyage Days</span>
                <span className="timeline-value">{data.voyage_days || '0'} Days</span>
              </div>
              <div className="timeline-row">
                <span className="timeline-label">Distance</span>
                <span className="timeline-value">{formatNumber(data.distance, 0)} NM</span>
              </div>
              {hasIceClass && data.ice_class_distance && (
                <div className="timeline-row">
                  <span className="timeline-label">Ice Class Distance</span>
                  <span className="timeline-value">{formatNumber(data.ice_class_distance, 0)} NM</span>
                </div>
              )}
              <div className="timeline-row">
                <span className="timeline-label">Average Speed</span>
                <span className="timeline-value">{formatNumber(data.speed_in_knots, 1)} Knots</span>
              </div>
              <div className="timeline-row">
                <span className="timeline-label">Total Emissions</span>
                <span className="timeline-value">{formatNumber(data.total_emission)} MT</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── FUEL TABLE ── */}
      <div className="data-table-wrapper">
        <div className="table-section">
          <h3 className="section-title">Fuel &amp; Emission Ledger</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Fuel</th>
                <th style={{ textTransform: "none" }}>SEA CONS. (MT)</th>
                <th style={{ textTransform: "none" }}>PORT CONS. (MT)</th>
                <th style={{ textTransform: "none" }}>UK PORT CONS. (MT)</th>
                {hasIceClass && <th style={{ textTransform: "none" }}>ICE CONS. (MT)</th>}
                <th style={{ textTransform: "none" }}>EMISSION (MTCO<sub>2</sub>)</th>
                <th>EUA</th>
                <th>UKA</th>
              </tr>
            </thead>
            <tbody>
              {data.table_data?.map((f, i) => (
                <tr key={i}>
                  <td className="fuel-name">{f.fuel_name}</td>
                  <td>{formatNumber(f.sea_consumption)}</td>
                  <td>{formatNumber(f.port_consumption)}</td>
                  <td>{formatNumber(f.uk_port_consumption || 0)}</td>
                  {hasIceClass && <td>{formatNumber(f.ice_class_consumption || 0)}</td>}
                  <td>{formatNumber(f.total_emission)}</td>
                  <td>{formatNumber(f.total_eua)}</td>
                  <td>{formatNumber(f.total_uka || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <div className="report-footer">
        <div className="footer-disclaimer-box">
          <div className="disclaimer-content">
            <div className="disclaimer-item">
              <span className="disclaimer-bullet">*</span>
              <span className="disclaimer-text">
                CII Value is calculated without fuel exclusions.&nbsp;
                This statement is based on vessel-specific data provided by the client through the web application.
                Validation checks have been carried out satisfactorily, resulting in the voyage outlined above.
                <strong> Azolla</strong> shall not be liable for any error, omission or deliberate falsification of
                client-provided data used in the preparation of this statement.
                This statement is prepared solely for the purpose specified herein for the named vessel and voyage.
                It does not release the vessel's manager from any obligation regarding MARPOL Annex VI and EU regulations.
                This statement does not substitute the annual verification statements.
              </span>
            </div>
          </div>
        </div>
        <div className="footer-divider" />
        <div className="footer-bottom-row">
          <div className="footer-meta">
            <img src="/icon.png" className="report-img" style={{ width: "20px", height: "20px" }} />
            <strong>&nbsp;&nbsp;Azolla</strong>
          </div>
          <div className="footer-center-block">
            <div className="footer-date">
              Report Generated:&nbsp;
              {new Date().toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
              })}
            </div>
            <div className="footer-copyright">
              © {new Date().getFullYear()} Azolla. All Rights Reserved. • Confidential &amp; Proprietary
            </div>
          </div>
        </div>
      </div>

      <div className="action-bar no-print">
        <button className="btn-save-pdf" onClick={downloadPDF}>Save as PDF</button>
      </div>
    </div>
  );
};

export default EmissionsReport;
